// Rapport de concordance avec le journal des ventes Dolibarr (12/10/2026) :
// compte par compte (70x, 419, 4457x, 411), TVA par taux, détail par facture
// si le journal porte les numéros. Le fichier n'est jamais conservé.
import { useState } from "react";
import api from "../services/api";

const eur = (n) => (n == null ? "—" : `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const okCell = (ok) => (ok ? <span style={{ color: "#16a34a" }}>✓</span> : <span style={{ color: "#b45309", fontWeight: 600 }}>écart</span>);

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = () => reject(new Error("Lecture du fichier impossible."));
  r.readAsDataURL(file);
});

export default function DolibarrConcordance({ companyId, month }) {
  const [payload, setPayload] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async (file) => {
    if (!file) return;
    setLoading(true); setError(""); setReport(null);
    try {
      const data = { month, filename: file.name, content_base64: await toBase64(file) };
      setPayload(data);
      setReport(await api.vatSummary.concordance(companyId, data));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const exportXlsx = async () => {
    try {
      const { blob, filename } = await api.vatSummary.concordanceXlsx(companyId, { ...payload, month });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e.message); }
  };

  const stale = report && report.period.month !== month;
  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 700 }}>Concordance avec Dolibarr</div>
        <div style={{ display: "flex", gap: 8 }}>
          <label className="btn" style={{ cursor: "pointer" }}>
            Choisir le journal des ventes Dolibarr
            <input type="file" accept=".csv,.txt,.xlsx,.xls" style={{ display: "none" }} onChange={e => { run(e.target.files[0]); e.target.value = ""; }} />
          </label>
          <button className="btn" onClick={exportXlsx} disabled={!report || stale}>⬇ Export Excel</button>
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "6px 0 10px" }}>
        Compare le journal des ventes Dolibarr du mois choisi avec les factures validées dans ExecPro360, compte par compte et au centime. Le fichier n'est pas conservé.
      </div>
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: "var(--text-muted)" }}>Comparaison…</div>}
      {stale && <div style={{ fontSize: 12.5, color: "#b45309", marginBottom: 8 }}>Le rapport porte sur {report.period.month} : choisissez à nouveau le journal pour {month}.</div>}
      {report && !loading && (
        <>
          <div style={{ padding: "8px 12px", borderRadius: 10, marginBottom: 10, fontSize: 13, fontWeight: 600, background: report.ok ? "#dcfce7" : "#fef3c7", color: report.ok ? "#166534" : "#92400e" }}>
            {report.ok ? "✓ Concordant au centime" : "⚠ Écarts à examiner"} — journal Dolibarr : {report.file.lines} lignes, {report.file.pieces} pièces · ExecPro360 : {report.execpro.invoices} facture(s) validée(s)
          </div>
          {report.notices.map((n, i) => <div key={i} style={{ fontSize: 12.5, color: "#92400e", marginBottom: 6 }}>⚠️ {n}</div>)}
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ width: "100%", fontSize: 12 }}>
              <thead><tr><th style={{ textAlign: "left" }}>Compte</th><th style={{ textAlign: "right" }}>Dolibarr</th><th style={{ textAlign: "right" }}>ExecPro360</th><th style={{ textAlign: "right" }}>Écart</th><th /></tr></thead>
              <tbody>
                {report.groups.map(g => [
                  ...g.rows.map(r => (
                    <tr key={g.key + r.account}>
                      <td>{r.group === "clients" ? `411 (${r.dolibarr_accounts} / ${r.execpro_accounts} sous-comptes)` : r.account}</td>
                      <td style={{ textAlign: "right" }}>{eur(r.dolibarr)}</td><td style={{ textAlign: "right" }}>{eur(r.execpro)}</td>
                      <td style={{ textAlign: "right" }}>{eur(r.gap)}</td><td>{okCell(r.ok)}</td>
                    </tr>
                  )),
                  <tr key={g.key + "-total"} style={{ fontWeight: 700, background: "var(--bg-subtle, #f8fafc)" }}>
                    <td>Total {g.label}</td><td style={{ textAlign: "right" }}>{eur(g.dolibarr)}</td><td style={{ textAlign: "right" }}>{eur(g.execpro)}</td>
                    <td style={{ textAlign: "right" }}>{eur(g.gap)}</td><td>{okCell(g.ok)}</td>
                  </tr>,
                ])}
              </tbody>
            </table>
          </div>
          <div style={{ fontWeight: 700, margin: "12px 0 6px" }}>TVA collectée par taux</div>
          <table className="table" style={{ width: "100%", fontSize: 12 }}>
            <thead><tr><th style={{ textAlign: "left" }}>Taux</th><th style={{ textAlign: "right" }}>Dolibarr</th><th style={{ textAlign: "right" }}>ExecPro360</th><th style={{ textAlign: "right" }}>Écart</th><th /></tr></thead>
            <tbody>{report.vat_by_rate.map(v => (
              <tr key={String(v.rate)}><td>{typeof v.rate === "number" ? `${v.rate} %` : v.rate}</td><td style={{ textAlign: "right" }}>{eur(v.dolibarr)}</td><td style={{ textAlign: "right" }}>{eur(v.execpro)}</td><td style={{ textAlign: "right" }}>{eur(v.gap)}</td><td>{okCell(v.ok)}</td></tr>
            ))}</tbody>
          </table>
          {report.invoices && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5 }}>Détail facture par facture ({report.invoices.length}, dont {report.invoices.filter(i => i.status !== "concordante").length} à examiner)</summary>
              <table className="table" style={{ width: "100%", fontSize: 12 }}>
                <thead><tr><th style={{ textAlign: "left" }}>Facture</th><th style={{ textAlign: "left" }}>Statut</th><th style={{ textAlign: "right" }}>TTC Dolibarr</th><th style={{ textAlign: "right" }}>TTC ExecPro360</th><th style={{ textAlign: "right" }}>Écart</th></tr></thead>
                <tbody>{report.invoices.map(i => (
                  <tr key={i.ref}><td>{i.ref}</td><td>{i.status}</td><td style={{ textAlign: "right" }}>{eur(i.dolibarr_ttc)}</td><td style={{ textAlign: "right" }}>{eur(i.execpro_ttc)}</td><td style={{ textAlign: "right" }}>{eur(i.gap_ttc)}</td></tr>
                ))}</tbody>
              </table>
            </details>
          )}
        </>
      )}
    </div>
  );
}
